import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useLayoutEffect, useEffect, Fragment, useMemo, useCallback, useState, } from 'react';
import { Button } from '@sas/nova-commons/components/Button';
import { Popover } from '@sas/nova-commons/components/Popover';
import { createChildren } from '../Box/createChildren';
import { setConstraints } from '../../store/birdLayout/BirdLayoutState';
import { PromptContainerState, } from '../../store/birdElement/promptContainerState';
import { selectAccessibilityLabelState, selectBirdElementsState, selectBirdPromptContainerState, selectChildKeys, selectStyleState, } from '../../store/selectors';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useI18n } from '../../locale/I18nContext';
import { fontStyles, textHeight } from '../utils/style';
import { useDesignTokens } from '@sas/nova-commons/theme';
import { useFontLoadedKey } from '../FontProvider';
import TextUtil from '@sas-dvr/ltjs-commons/util/TextUtil';
import { getFirstFocusable } from '@sas/nova-commons/util/focus';
// NOVACOMMONS-11787: SelectBoxDesign is private, but we have permission to use it
// eslint-disable-next-line no-restricted-imports
import { SelectBoxDesign } from '@sas/nova-commons/components/Select/__internal__/SelectBoxDesign';
import { useCustomReportAreaOverlay } from '../CustomOverlay';
import { createUseHtmlId } from '@sas/nova-commons/hooks/useHtmlId';
import { usePromptContainerPopoverRoot } from './PromptContainerPopoverRoot';
import { tokensToStyle } from '../utils/tokensToStyle';
import { SelectionsContent } from './__internal__/SelectionsContent';
import { PromptLayoutDataProvider } from '../PromptLayoutDataProvider';
const usePopoverId = createUseHtmlId('PromptContainer-popover');
const useMouseHover = (mouseOverDelay = 500, mouseOutDelay = 100) => {
    const [mouseOver, setIsMouseOver] = useState(false);
    const callbackId = useRef(0);
    const mouseOverActual = useRef(false);
    useEffect(() => () => {
        if (callbackId.current) {
            clearTimeout(callbackId.current);
        }
    }, []);
    return [
        useCallback(function mouseover() {
            mouseOverActual.current = true;
            if (callbackId.current) {
                clearTimeout(callbackId.current);
                callbackId.current = 0;
            }
            callbackId.current = window.setTimeout(() => {
                setIsMouseOver((state) => (state === 'disabled' ? 'disabled' : true));
            }, mouseOverDelay);
        }, [mouseOverDelay]),
        useCallback(function mouseout() {
            mouseOverActual.current = false;
            if (callbackId.current) {
                clearTimeout(callbackId.current);
                callbackId.current = 0;
            }
            callbackId.current = window.setTimeout(() => {
                setIsMouseOver((state) => (state === 'disabled' ? 'disabled' : false));
            }, mouseOutDelay);
        }, [mouseOutDelay]),
        // isMouseOver: false if the mouse is not over or if the utility hook is disabled and
        //   true if the mouse is over and the utility hook is enabled.
        mouseOver === 'disabled' ? false : mouseOver,
        useCallback(function setEnabled(enabled) {
            if (!enabled) {
                setIsMouseOver('disabled');
                if (callbackId.current) {
                    clearTimeout(callbackId.current);
                    callbackId.current = 0;
                }
                return;
            }
            setIsMouseOver(false);
            if (mouseOverActual.current) {
                callbackId.current = window.setTimeout(() => {
                    setIsMouseOver(true);
                }, mouseOverDelay);
            }
        }, [mouseOverDelay]),
    ];
};
export const PromptContainerBase = (props) => {
    var _a, _b, _c, _d;
    const { label, isHorizontal, autoApplyValues, isOpen, autoClose, focusLockProps, autoFocus, } = props.state;
    const actions = props.actions;
    const localize = useI18n();
    const refButton = useRef(null);
    const refContent = useRef(null);
    const refChangeButtons = useRef(null);
    const [onMouseEnter, onMouseLeave, selectionPopoverOpen, setEnabled] = useMouseHover();
    useEffect(() => {
        setEnabled(!isOpen);
    }, [setEnabled, isOpen]);
    const { font } = props.style || {};
    const dt = useDesignTokens();
    const fonts = useMemo(() => fontStyles(font, dt), [font, dt]);
    const fontKey = useFontLoadedKey(fonts.fontFamily);
    useLayoutEffect(() => {
        const height = Math.max(28 /* min height 1.75rem */, textHeight(fonts) + 8 /* padding */ + 2 /* border */);
        const width = TextUtil.getTextWidthFromComputedStyle(label || '', fonts) +
            18 + // Icon width
            16 + // left and right padding
            2; // left and right border
        actions.setConstraints(props.id, {
            minWidth: width,
            preferredWidth: width,
            minHeight: height,
            preferredHeight: height,
        });
    }, [fontKey /*(implicit)*/, actions, props.id, fonts, label]);
    useEffect(() => {
        var _a;
        if (refContent.current && autoFocus) {
            (_a = getFirstFocusable(refContent.current)) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [isOpen, autoFocus]);
    // Apply all prompt container style overrides to the button bar except
    // background color, which has its own override. This uses tokensToStyle
    // because Button doesn't handle font overrides through props.
    const buttonBarStyle = Object.assign(Object.assign({}, tokensToStyle(props.style)), { backgroundColor: (_b = (_a = props.buttonBarStyle) === null || _a === void 0 ? void 0 : _a.normal) === null || _b === void 0 ? void 0 : _b.bg });
    // Color can only be set through the tokens prop
    const buttonTokens = useCallback(() => {
        var _a, _b, _c, _d;
        const tokens = {};
        if ((_b = (_a = props.style) === null || _a === void 0 ? void 0 : _a.normal) === null || _b === void 0 ? void 0 : _b.fg) {
            tokens.fg = (_d = (_c = props.style) === null || _c === void 0 ? void 0 : _c.normal) === null || _d === void 0 ? void 0 : _d.fg;
        }
        return tokens;
    }, [props.style]);
    const promptContainerRoot = usePromptContainerPopoverRoot();
    const CustomReportAreaOverlay = useCustomReportAreaOverlay();
    const popoverId = usePopoverId();
    const refReturnFocus = useRef(false);
    const closePopover = (action, returnFocus) => {
        var _a;
        if (action === 'ok') {
            props.actions.notifyApplyChanges(props.id);
        }
        else if (action === 'cancel') {
            props.actions.notifyCancelChanges(props.id);
        }
        actions.setOpen(props.id, false);
        if (returnFocus) {
            if (focusLockProps === null || focusLockProps === void 0 ? void 0 : focusLockProps.disabled) {
                (_a = refButton.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
            else {
                refReturnFocus.current = true;
            }
        }
    };
    let dismissAction;
    const actionButtons = [];
    if (autoApplyValues) {
        dismissAction = 'close';
        if (!autoClose) {
            actionButtons.push(_jsx(Button, { text: localize({ key: 'PromptContainer.close.txt' }), onPress: () => {
                    closePopover('close', true);
                }, tokens: buttonTokens }, "close"));
        }
    }
    else {
        dismissAction = 'cancel';
        actionButtons.push(_jsx(Button, { text: localize({ key: 'PromptContainer.apply.txt' }), onPress: () => {
                closePopover('ok', true);
            }, tokens: buttonTokens }, "ok"));
        actionButtons.push(_jsx(Button, { text: localize({ key: 'PromptContainer.cancel.txt' }), onPress: () => {
                closePopover('cancel', true);
            }, tokens: buttonTokens }, "cancel"));
    }
    // Render empty prompt containers with extra room for drag-and-drop
    let contentAreaSize;
    if (!((_c = props.childKeys) === null || _c === void 0 ? void 0 : _c.length)) {
        contentAreaSize = isHorizontal ? { width: 400, height: 100 } : { width: 300, height: 300 };
    }
    return (_jsxs(Fragment, { children: [_jsx("div", Object.assign({ onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }, { children: _jsx(SelectBoxDesign, { label: label, font: font, theme: () => { var _a; return (_a = props.style) === null || _a === void 0 ? void 0 : _a.normal; }, innerRef: refButton, onPress: () => props.actions.toggleOpen(props.id), onKeyDown: (e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            props.actions.toggleOpen(props.id);
                        }
                    }, isOpen: isOpen, ariaLabel: props.accessibilityLabel, listId: popoverId }) })), _jsxs(Popover, Object.assign({ id: popoverId, theme: () => { var _a; return (_a = props.style) === null || _a === void 0 ? void 0 : _a.normal; }, isModal: autoClose && !autoApplyValues, isOpen: isOpen, onClose: autoClose ? () => closePopover(dismissAction, false) : undefined, style: {
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: '6.25rem',
                    minHeight: '2rem',
                    padding: 0,
                }, focusable: false, referenceElementRef: refButton, placement: "bottom", returnFocus: false, focusLockProps: Object.assign({ onDeactivation() {
                        if (refReturnFocus.current) {
                            refReturnFocus.current = false;
                            setTimeout(() => {
                                var _a;
                                (_a = refButton.current) === null || _a === void 0 ? void 0 : _a.focus();
                            });
                        }
                    } }, focusLockProps), onEscape: () => {
                    refReturnFocus.current = true;
                }, popoverRoot: promptContainerRoot, allowOnContextMenuPropagation: true, 
                // Disable the default animation since the popover never becomes visible in an OpenUI test in CI
                animation: {
                    variants: undefined,
                    initial: undefined,
                    animate: undefined,
                    exit: undefined,
                } }, { children: [_jsxs("div", Object.assign({ style: Object.assign({ position: 'relative', flex: '1 1 auto', minHeight: 0, maxWidth: '100%' }, contentAreaSize) }, { children: [_jsx("div", Object.assign({ ref: refContent, style: {
                                    height: '100%',
                                    overflow: 'auto',
                                    display: 'flex',
                                    flexDirection: isHorizontal ? 'row' : 'column',
                                    alignItems: 'flex-start',
                                }, role: "document" }, { children: _jsx(PromptLayoutDataProvider, Object.assign({ direction: isHorizontal ? 'horizontal' : 'vertical', crossAxisOnly: true }, { children: createChildren(props.childKeys, props.elementState, {
                                        includePreferredSizing: true,
                                        style: Object.assign({ maxWidth: '100%', maxHeight: '350px' }, (isHorizontal ? { flexShrink: 1 } : undefined)),
                                        contentJustification: 'normal',
                                    }) })) })), CustomReportAreaOverlay && (_jsx(CustomReportAreaOverlay, { id: props.id, type: "prompt-container" }))] })), actionButtons.length ? (_jsx("div", Object.assign({ ref: refChangeButtons, style: Object.assign({ padding: '1rem', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: '1rem' }, buttonBarStyle) }, { children: actionButtons }))) : null] })), _jsx(Popover, Object.assign({ theme: () => { var _a; return (_a = props.style) === null || _a === void 0 ? void 0 : _a.normal; }, style: { '--popover-padding': '.5rem' }, placement: "auto-end", hideArrow: true, focusable: false, referenceElementRef: refButton, isOpen: !!((_d = props.childKeys) === null || _d === void 0 ? void 0 : _d.length) && selectionPopoverOpen }, { children: _jsx(SelectionsContent, { style: props.style, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, id: props.id }) }))] }));
};
export const PromptContainer = connect((state, props) => ({
    state: selectBirdPromptContainerState(state, props.id),
    childKeys: selectChildKeys(state, props.id),
    elementState: selectBirdElementsState(state),
    style: selectStyleState(state, props.id),
    buttonBarStyle: selectStyleState(state, props.id + '-buttonbar'),
    accessibilityLabel: selectAccessibilityLabelState(state, props.id),
}), (dispatch) => ({
    actions: bindActionCreators(Object.assign({ setConstraints }, PromptContainerState.actions), dispatch),
}))(PromptContainerBase);
